import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function Login() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    if (user) return <Navigate to="/" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email.trim(), password);
            toast.success("¡Bienvenido!");
            navigate("/");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2 bg-background">
            <div className="hidden md:flex relative overflow-hidden bg-primary text-primary-foreground p-12 flex-col justify-between">
                <div className="absolute inset-0 opacity-15"
                     style={{ backgroundImage: "url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=85&w=2000&fit=crop')", backgroundSize: "cover", backgroundPosition: "center" }} />
                <div className="relative">
                    <img
                        src="/foresta-logo-white.png"
                        alt="Foresta — Oficinas Corporativas"
                        className="w-48 h-auto object-contain"
                    />
                </div>
                <div className="relative">
                    <h2 className="font-heading text-4xl font-bold tracking-tight max-w-md">
                        Gestioná tu edificio con calma y orden.
                    </h2>
                    <p className="mt-4 text-sm opacity-90 max-w-sm">
                        Salas, reclamos, comunicados y visitas en un solo lugar. Diseñado para responsables,
                        recepción y operaciones del edificio Foresta, Mendoza.
                    </p>
                </div>
                <div className="relative text-xs opacity-75">
                    Mendoza, Argentina · {new Date().getFullYear()}
                </div>
            </div>

            <div className="flex items-center justify-center p-6 md:p-12">
                <Card className="w-full max-w-md border-border">
                    <CardContent className="p-8">
                        <img src="/foresta-logo.png" alt="Foresta — Oficinas Corporativas" className="md:hidden h-20 w-auto object-contain mb-4" />
                        <h1 className="font-heading text-2xl font-bold tracking-tight">Iniciar sesión</h1>
                        <p className="text-sm text-muted-foreground mt-1">Accedé con tu cuenta de Foresta.</p>
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input id="email" data-testid="login-email" type="email" autoComplete="email"
                                       value={email} onChange={(e) => setEmail(e.target.value)} required />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Contraseña</Label>
                                <Input id="password" data-testid="login-password" type="password"
                                       autoComplete="current-password"
                                       value={password} onChange={(e) => setPassword(e.target.value)} required />
                            </div>
                            <Button type="submit" data-testid="login-submit" className="w-full" disabled={busy}>
                                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Ingresar
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
